import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ApplicationFormDetails } from "../pages/Application/SteppedApplicationContainer";
import { useLocation, useParams } from "react-router-dom";
import useMediaQuery from "../../hooks/useMediaQuery";
import { ModalContainer } from "../shared/ModalContainer";
import useMountTransition from "../../hooks/useMountTransition";
import { ApplicationStep } from "@/enum/ApplicationStep";
import { ApplicationStepStatus } from "@/enum/ApplicationStepStatus";
import StepIcon, { StepIconSize } from "./StepIcon";
import TransitionLink from "../TransitionLink";
import { isDebugForms } from "@/util/debug";

interface ApplicationFormDetailsStatus extends ApplicationFormDetails {
  state?: ApplicationStepStatus;
  isCurrent?: boolean;
}

const formatSteps = (
  steps: ApplicationFormDetails[],
  stepStatuses: Record<ApplicationStep, ApplicationStepStatus>,
  pathname: string,
): ApplicationFormDetailsStatus[] => {
  const debug = isDebugForms();
  return steps.map((step, i) => {
    let state = stepStatuses?.[step.step] ?? ApplicationStepStatus.Disabled;
    if (debug && state === ApplicationStepStatus.Disabled) {
      state = ApplicationStepStatus.Enabled;
    }

    const isCurrent = pathname.includes(step.path);

    const formatted: ApplicationFormDetailsStatus = {
      ...step,
      state,
      isCurrent,
    };

    if (step.children) {
      formatted.children = formatSteps(step.children, stepStatuses, pathname);
    }

    return formatted;
  });
};

interface FormProgressBarProps {
  activeStepIndex: number;
  steps: ApplicationFormDetails[];
  stepStatuses: Record<ApplicationStep, ApplicationStepStatus>;
}

const FormProgressBar = (props: FormProgressBarProps) => {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery("767px");

  const visibleSteps = formatSteps(props.steps, props.stepStatuses, pathname);

  return (
    <nav aria-label="Progress">
      {isMobile ? (
        <MobileStepperComponent steps={visibleSteps} />
      ) : (
        <div className="space-y-1">
          {visibleSteps.map((step, i) => (
            <StepItem key={i} step={step} isLast={i === visibleSteps.length - 1} />
          ))}
        </div>
      )}
    </nav>
  );
};

const StepItem = ({ step, isLast }: { step: ApplicationFormDetailsStatus; isLast: boolean }) => {
  const { id } = useParams();
  const isDisabled = step.state === ApplicationStepStatus.Disabled;
  const isComplete = step.state === ApplicationStepStatus.Complete;
  const isCurrent = step.isCurrent || false;
  const hasChildren = step.children && step.children.length > 0;
  const hasCurrentChild = hasChildren && step.children!.some((c: any) => c.isCurrent);
  const Icon = step.icon;

  const linkTo = (!isDisabled || isDebugForms()) && step.path ? `/application/${id}${step.path}` : "#";

  return (
    <div>
      <TransitionLink to={linkTo}>
        <div
          className={cn(
            "relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            isCurrent && !hasCurrentChild && "bg-emerald-50 text-emerald-700",
            hasCurrentChild && "text-emerald-700",
            isComplete && !isCurrent && !hasCurrentChild && "text-gray-700 hover:bg-gray-50",
            !isCurrent && !hasCurrentChild && !isComplete && !isDisabled && "text-gray-600 hover:bg-gray-50",
            isDisabled && !isCurrent && "text-gray-400 cursor-default",
          )}
        >
          {isCurrent && !hasCurrentChild && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-emerald-500" />
          )}
          {Icon && (
            <Icon
              className={cn(
                "size-4 flex-shrink-0",
                (isCurrent || hasCurrentChild) && "text-emerald-600",
                isComplete && !isCurrent && !hasCurrentChild && "text-emerald-500",
                isDisabled && !isCurrent && "text-gray-300",
              )}
            />
          )}
          <span
            className={cn(
              "text-sm font-medium flex-1 min-w-0 truncate",
              (isCurrent || hasCurrentChild) && "font-semibold",
            )}
          >
            {step.name}
          </span>
          {isComplete && !isCurrent && (
            <StepIcon stepStatus={ApplicationStepStatus.Complete} size={StepIconSize.Small} />
          )}
          {isCurrent && !hasCurrentChild && <ChevronRight className="size-4 text-emerald-400 flex-shrink-0" />}
        </div>
      </TransitionLink>
      {hasChildren && (
        <div className="ml-5 pl-3 border-l-2 border-gray-200 space-y-0.5 mt-0.5 mb-1">
          {step.children!.map((child, i) => (
            <ChildStepItem key={i} step={child as ApplicationFormDetailsStatus} />
          ))}
        </div>
      )}
    </div>
  );
};

const ChildStepItem = ({ step }: { step: ApplicationFormDetailsStatus }) => {
  const { id } = useParams();
  const isDisabled = step.state === ApplicationStepStatus.Disabled;
  const isComplete = step.state === ApplicationStepStatus.Complete;
  const isCurrent = step.isCurrent || false;
  const Icon = step.icon;

  const linkTo = (!isDisabled || isDebugForms()) && step.path ? `/application/${id}${step.path}` : "#";

  return (
    <TransitionLink to={linkTo}>
      <div
        className={cn(
          "relative flex items-center gap-2.5 px-2.5 py-1.5 rounded-md transition-colors text-[13px]",
          isCurrent && "bg-emerald-50 text-emerald-700 font-semibold",
          isComplete && !isCurrent && "text-gray-600 hover:bg-gray-50",
          !isCurrent && !isComplete && !isDisabled && "text-gray-500 hover:bg-gray-50",
          isDisabled && !isCurrent && "text-gray-400 cursor-default",
        )}
      >
        {isCurrent && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-emerald-500" />
        )}
        {Icon && (
          <Icon
            className={cn(
              "size-4 flex-shrink-0",
              isCurrent && "text-emerald-600",
              isComplete && !isCurrent && "text-emerald-500",
              isDisabled && !isCurrent && "text-gray-300",
            )}
          />
        )}
        <span className="flex-1 min-w-0 truncate">{step.name}</span>
        {isComplete && !isCurrent && <StepIcon stepStatus={ApplicationStepStatus.Complete} size={StepIconSize.Small} />}
      </div>
    </TransitionLink>
  );
};

const MobileStepperComponent = ({ steps }: { steps: ApplicationFormDetailsStatus[] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasTransitionedIn = useMountTransition(isModalOpen, 150);

  const activeStep = steps.find((s) => s.isCurrent);

  return (
    <>
      <button
        className="flex items-center justify-between w-full p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => setIsModalOpen(true)}
      >
        <span className="text-sm font-medium text-gray-700">
          {activeStep ? activeStep.name : "Application Progress"}
        </span>
        <ChevronDown className="size-4 text-gray-500" />
      </button>
      {(hasTransitionedIn || isModalOpen) && (
        <ModalContainer
          onClose={() => setIsModalOpen(false)}
          active={hasTransitionedIn && isModalOpen}
          title="Application progress"
        >
          <div className="space-y-1 py-2">
            {steps.map((step, i) => (
              <StepItem key={i} step={step} isLast={i === steps.length - 1} />
            ))}
          </div>
        </ModalContainer>
      )}
    </>
  );
};

export default FormProgressBar;
